/**
 * @file AuthContext.tsx
 * @description React Context для состояния авторизации (phase04 шаг 3).
 *
 * Само значение контекста определено здесь; провайдер (AuthProvider) и
 * хуки (useAuth, useCurrentContext, ...) — в соседних файлах, чтобы их
 * можно было тестировать изолированно.
 *
 * Хранимое значение:
 * - status: 'loading' (первый /auth/me ещё идёт), 'authenticated', 'unauthenticated'.
 * - me: ответ /auth/me (или null).
 * - login/logout/refresh/switchContext: мутации, обновляют состояние внутри.
 * - tabContextId/selectTabContext: кабинет вкладки (phase19.31, SC21–SC23).
 */

import { createContext } from "react";

import type { MeResponse } from "./types";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthContextValue {
  status: AuthStatus;
  me: MeResponse | null;
  /** Логин по email/password — обновит me и вернёт ответ. Бросит ApiError при неуспехе. */
  login(input: { email: string; password: string }): Promise<MeResponse>;
  /** Logout — уничтожает сессию на API и сбрасывает me. */
  logout(): Promise<void>;
  /** Сменить активный AccountContext (если у юзера их несколько). */
  switchContext(contextId: string): Promise<MeResponse>;
  /** Принудительно перечитать /auth/me (после CRUD-операций над контекстами). */
  refresh(): Promise<void>;
  /**
   * Кабинет ЭТОЙ вкладки (phase19.31, SC21): его id уходит заголовком
   * `X-Commora-Context` в каждом запросе, а `me.activeContext` показывает
   * именно его. `null` — вкладка работает по кабинету сессии.
   */
  tabContextId: string | null;
  /** Выбрать кабинет вкладки (по адресу страницы); на сессию не влияет. */
  selectTabContext(contextId: string | null): void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
AuthContext.displayName = "AuthContext";
